import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import React, {useEffect, useImperativeHandle, useState} from "react";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import {DATE_TIME_FORMAT_NAME_MODEL, errorMsg, getDateFormat, getFullName} from "../../../../../Apis/functions";
import {
    CancelIconsOptions, DetailsIconsOptions,
    IconClockTable,
    IconMarkerTable,
    IconScheduleTable,
    NextIconsOptions
} from "../../../../../components/buttons/IconButtons";
import CustomChip from "../../../../../components/CustomChip";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import {status, STATUS_MODEL} from "../../../../../enums/statusEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import {permissionObj} from "../../../../../Helpers/permission";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import routes from "../../../../../Routes/routes";
import {TABLES} from "../../../../../Apis/Tables";
import {EVENT_STATUS, RESERVATIONS_SUB_VIEW_MODEL, TABS_RESERVATIONS} from "../../Model/Model";
import {ACTION_MODEL} from "../../../../../Helpers/ActionModel";
import TableFunction from "../../../../../components/table/TableFunction";
import {toast} from "react-toastify";
import {
    DEFAULT_LEFT,
    LEFT_FIRST_COLUMN_WITH_CHECKBOX,
    SEARCH_MODEL
} from "../../../../../components/table/Model/TableModel";
import {SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {VIEW_TYPE} from "../../../../../enums/ViewType";
import {clearSearchFilters} from "../../../../../reduxToolkit/Search/SearchSlice";
import {cancelEventByList, deleteEvents} from "../../../../../reduxToolkit/EmployeeEvents/EmployeeEventsApi";
import {invitationInTime} from "../../../Visitor/Invitation/Model/Model";
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 * ReservationsStatusTableApp component
 * -----------------------------------
 * Displays a table showing reservation statuses with support for
 * filtering, searching, pagination, and user-controlled actions (view details, cancel, etc.).
 *
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 * @param {Array} selectedList - Array of currently selected items in the table.
 * @param {Function} setSelectedList - Function to update the selectedList state.
 * @param {Function} setBodyForExport - Function to set the body object for exporting data.
 * @param {Array} finalArray - Array of final criteria used for filtered search.
 * @param {Function} setSearch - Function to update the search state.
 * @param {any} option - Option used in search and filtering.
 * @param {React.Ref} ref - Reference object to expose internal methods to parent.
 * @param {Function} setCriteria - Function to define current search criteria.
 * @param searchElement
 */

export const ReservationsStatusTableApp = ({
                                               selectedList,
                                               setSelectedList,
                                               setBodyForExport,
                                               finalArray,
                                               setSearch,
                                               option,
                                               ref,
                                               setCriteria,
                                               searchElement
                                           }) => {

    // Redux and router hooks
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // Table identifiers and state
    const tableId = "tbl_rst_events";
    const searchId = "txt_rst_events";

    // Permissions and layout
    const showCheckbox = (DEV || permission?.includes(permissionObj?.WEB_EVENT_STATUS_DELETE) || permission?.includes(permissionObj?.WEB_EVENT_STATUS_CANCEL));
    const leftPosition = showCheckbox ? LEFT_FIRST_COLUMN_WITH_CHECKBOX : DEFAULT_LEFT;

    // Component state
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [results, setResults] = useState(null);
    const [modified, setModified] = useState(false);

    /**
     * Defines table header columns.
     * Each column is represented by a CustomTh component.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition} classes="first_head">{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('host')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('access_zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('start_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('end_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('booking_status')}</CustomTh>
        ];
    };

    /**
     * Generates table body rows based on fetched results.
     * Each row includes reservation data such as host, dates, and current status.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {body: [], data: []};

        const body = results?.content?.map((item) => {
            const customStatusId = !invitationInTime(item?.endDate) ? STATUS_MODEL.OVERDUE : item?.statusId;
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={'first-column'}>
                        <CustomTooltip
                            text={item?.name}
                            classes={'fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis'}
                            showIfTruncated={true}
                            placement={'top'}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomTooltip
                            text={getFullName(item.hostName, item.hostLastName, item.hostSecondLastName)}
                            classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                            showIfTruncated={true}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className={'d-flex align-items-center'}>
                            <div>
                                <IconMarkerTable className={'mr-8px mb-1'} />
                            </div>
                            <CustomTooltip
                                text={item.reservationZoneName}
                                classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis`}
                                showIfTruncated={true}
                            />
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div className='d-flex align-items-center'>
                            <div><IconScheduleTable className='mr-8px mb-1 icon-color-general-950' /></div>
                            {getDateFormat(item?.startDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        </div>
                        <div className='d-flex align-items-center'>
                            <div><IconClockTable className='mr-8px mb-1 icon-color-general-950' /></div>
                            {item.reservationFromTime}
                        </div>
                    </CustomTd>

                    <CustomTd tableId={tableId}>
                        <div className='d-flex align-items-center'>
                            <div><IconScheduleTable className='mr-8px mb-1 icon-color-general-950' /></div>
                            {getDateFormat(item?.endDate, false, DATE_TIME_FORMAT_NAME_MODEL.shortMonth)}
                        </div>
                        <div className='d-flex align-items-center'>
                            <div><IconClockTable className='mr-8px mb-1 icon-color-general-950' /></div>
                            {item.reservationToTime}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            background={statusColor[customStatusId]}
                            fontColor={statusFontColor[customStatusId]}
                            borderColor={statusBorderColor[customStatusId]}
                            legend={t(status[customStatusId])}
                            variant={'large'}
                        />
                    </CustomTd>
                </>
            );});

        return {body, data: results?.content};
    };

    /**
     * Renders the action buttons for each row, allowing navigation
     * to details or triggering cancelation actions.
     * Buttons are conditionally displayed based on user permissions.
     */
    const getActionsButtons = (item, index) => {
        const inTime = invitationInTime(item?.endDate);

        return (
            <DropDownMenuProfile openMenu={openMenu} handleMenuToggle={handleMenuToggle} menuId={`menu-${item.id}`}>
                {item?.approvalFlowId && (
                    <DropDownMenuItem
                        id={'btn_rst_approval_tracker_flow' + item.id}
                        index={item.id}
                        handleClose={handleClose}
                        name={t('approval_manager')}
                        url={{
                            pathname: routes.approvalTracker.details + TABLES.EVENT + '/' + item?.id + '/' + item?.approvalFlowId,
                            state: {
                                backUrl: {
                                    pathname: routes.event.list,
                                    state: {
                                        currentTab: TABS_RESERVATIONS.BOOKINGS.INDEX,
                                        currentSubView: RESERVATIONS_SUB_VIEW_MODEL.BOOKINGS.STATUS,
                                        isOverdue: !inTime
                                    }
                                }
                            }
                        }}
                        icon={<NextIconsOptions/>}
                    />
                )}
                <DropDownMenuItem
                    id={'btn_rsta_details_' + item.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    click={() => handleViewDetails(item)}
                    icon={<DetailsIconsOptions />}
                />

                {EVENT_STATUS.cancel.includes(item?.statusId) &&
                    (DEV || permission?.includes(permissionObj?.WEB_EVENT_STATUS_CANCEL)) &&
                    inTime && (
                    <DropDownMenuItem
                        id={"btn_eta_cancel_" + item.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('cancel')}
                        click={() => handleShowModals(ACTION_MODEL.CANCEL, item)}
                        icon={<CancelIconsOptions/>}
                        fontColor='font-color-error-600'
                        iconColor={"icon-color-error-code-600"}
                    />
                )}
            </DropDownMenuProfile>
        );
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            criteria: [],
            sortBy: "id",
            order: true,
            key: searchElement,
            viewType: VIEW_TYPE.LIST.viewType
        });
        setBodyForExport(body);
        dispatch(SimpleSearch({body})).then(({payload: {data: {data, success}}}) => {
            if (success === true) {
                setResults(data);
            }
        });
    }

    // TableFunction hook - Handles pagination, checkbox selection, and table data rendering.
    const {t, thead, tbody, handleCheckboxChange, handleDeleteAll, openMenu, handleMenuToggle, handleClose, pagination, page, rowsPerPage} =
        TableFunction({
            selectedList,
            setSelectedList,
            data: results,
            getThead,
            getTbody,
            labelRowsPerPage: 'reservations_per_page',
            setIsAllChecked,
            hasContainer: true,
            fixedPagination: false,
            searchElement, //New
            getData,//New
            modified,//New
            setModified,//New
        });

    /**
     * Handles navigation to the reservation details page.
     */
    const handleViewDetails = (event) => {
        navigate(`${routes.event.details}${event?.id}`, {
            state: {
                backUrl: {
                    pathname: routes.event.list,
                    state: {
                        currentTab: TABS_RESERVATIONS.BOOKINGS.INDEX,
                        currentSubView: RESERVATIONS_SUB_VIEW_MODEL.BOOKINGS.STATUS
                    }
                }
            }
        });
    };

    /**
     * Cleanup hook - Clears all search filters from Redux when component unmounts.
     */
    useEffect(() => {
        return () => {
            dispatch(clearSearchFilters());
        };
    }, []);

    /**
     * Exposes handleShowModals to the parent component through ref.
     */
    useImperativeHandle(ref, () => ({
        handleShowModals,
    }));

    /**
     * Displays the corresponding modal based on the given action (e.g., cancel).
     */
    const handleShowModals = (action, event) => {
        if (event !== null && typeof event !== 'undefined') {
            setSelectedList([event.id]);
        }
        const modalActions = {
            [ACTION_MODEL.CANCEL]: () => setShowCancelModal(true),
            [ACTION_MODEL.DELETE]: () => setShowDeleteModal(true),
        };
        modalActions[action]?.();
    };

    /**
     * Hides all modals and resets selection.
     */
    const handleHideModals = () => {
        setIsAllChecked(false);
        setSelectedList([]);
        setShowCancelModal(false);
        setShowDeleteModal(false);
    };

    /**
     * Cancels one or more reservations.
     * Dispatches cancel action and displays success or error messages.
     */
    const handleCancelEventByList = () => {
        dispatch(cancelEventByList(selectedList)).then(({payload: {data: {success, code}}}) => {
            if (success === true) {
                setShowCancelModal(false);
                setModified(true);
                setIsAllChecked(false);
                setSelectedList([]);
                toast.success(t('has_been_cancelled_successfully'));
            } else {
                errorMsg(code);
            }
        });
    };

    /**
     * Deletes one or more reservations.
     * Dispatches the delete action and displays success or error messages.
     */
    const handleDeleteEventByList = () => {
        if (selectedList?.length === 0) return;

        dispatch(deleteEvents(selectedList)).then(({payload: {data: {success, code}}}) => {
            if (success === true) {
                setShowDeleteModal(false);
                setModified(true);
                setIsAllChecked(false);
                setSelectedList([]);
                toast.success(t('has_been_deleted_successfully'));
            } else {
                errorMsg(code);
            }
        });
    };

    // Return object exposing table configuration and state management methods.
    return {
        tableId,
        searchId,
        thead,
        tbody,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        page,
        rowsPerPage,
        setResults,
        results,
        isAllChecked,
        showCancelModal,
        showDeleteModal,
        handleHideModals,
        handleCancelEventByList,
        handleDeleteEventByList,
        t,
        modified,
        setModified,
        showCheckbox
    };
};
